import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal from "@/components/Reveal";
import { useLang } from "@/i18n";

const baseChapters = [
  {
    n: "01",
    title: "Sophrology",
    tag: "Dynamic relaxation",
    text: "A structured blend of guided breathing, gentle movement and positive visualisation that restores harmony between body and mind — a practical toolkit you keep for life.",
    img: "https://images.unsplash.com/photo-1616749314838-c8e22eebb15c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxjYWxtJTIwd29tYW4lMjBsYXlpbmclMjBpbiUyMGdyYXNzfGVufDB8fHx8MTc4OTIzOTY0Mnww&ixlib=rb-4.1.0&q=85",
    alt: "Woman resting peacefully in the grass",
  },
  {
    n: "02",
    title: "Reiki",
    tag: "Energy healing",
    text: "A gentle Japanese practice where light, resting hands encourage deep relaxation, release accumulated tension and support your body's natural ability to rebalance.",
    img: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?q=80&w=1600&auto=format&fit=crop",
    alt: "Relaxing hands-on wellness treatment",
  },
  {
    n: "03",
    title: "Mindfulness",
    tag: "Present-moment awareness",
    text: "Simple meditation and grounding techniques that quiet mental chatter, sharpen focus and help you meet each moment — at work, at school, at home — with clarity.",
    img: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=1600&auto=format&fit=crop",
    alt: "Person meditating at the beach at golden hour",
  },
];

export default function Chapters() {
  const [active, setActive] = useState(0);
  const { t } = useLang();
  const chapters = baseChapters.map((c, i) => ({ ...c, ...t.chapters.items[i] }));

  return (
    <section id="practices" className="bg-sand py-24 lg:py-36" data-testid="practices-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <p className="mb-6 text-xs font-semibold uppercase tracking-[0.3em] text-stone">{t.chapters.eyebrow}</p>
          <h2 className="max-w-2xl font-serif text-4xl leading-tight tracking-tight md:text-5xl lg:text-6xl">
            {t.chapters.h2a}<em className="italic font-light text-moss">{t.chapters.h2b}</em>
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-8">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div
                  onMouseEnter={() => setActive(i)}
                  className="group cursor-pointer border-t border-forest/15 py-10 transition-all duration-500 last:border-b hover:pl-4 md:py-12"
                  data-testid={`chapter-${c.title.toLowerCase()}`}
                >
                  <div className="flex items-baseline gap-6 md:gap-10">
                    <span className="font-serif text-lg italic text-stone">{c.n}</span>
                    <div>
                      <h3 className="font-serif text-4xl tracking-tight transition-colors duration-300 group-hover:text-moss md:text-6xl">
                        {c.title}
                      </h3>
                      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.25em] text-stone">{c.tag}</p>
                      <p className="mt-4 max-w-xl text-sm leading-relaxed text-moss md:text-base">{c.text}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="hidden lg:col-span-4 lg:block">
            <div className="sticky top-32 overflow-hidden rounded-full rounded-br-[6rem] border border-forest/10">
              <AnimatePresence mode="wait">
                <motion.img
                  key={active}
                  src={chapters[active].img}
                  alt={chapters[active].alt}
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="aspect-[3/4] w-full object-cover"
                  data-testid="chapter-image"
                />
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
